"use client";

import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import {
  Button,
  Input,
  MODAL_IDS,
  ResponsiveCommandSelect,
  ResponsiveModal,
  useUniversalAuth,
} from "@wtc/ui";
import { Loader2, Slack } from "lucide-react";
import type { Message } from "@/lib/core/crm/types";
import { useInboxes } from "@/lib/hooks/messages/useInboxes";
import { useMessageAttachments } from "@/lib/hooks/messages/useMessageAttachments";
import { AttachmentSelectionList } from "./AttachmentSelectionList";

interface SlackChannel {
  id: string;
  name: string;
  is_member?: boolean;
  is_private?: boolean;
}

interface ShareAttachmentsToSlackModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  message: Message;
  globalModalId?: string;
}

export function ShareAttachmentsToSlackModal({
  open,
  onOpenChange,
  message,
  globalModalId = MODAL_IDS.messages.shareAttachmentsToSlack,
}: ShareAttachmentsToSlackModalProps) {
  const { session } = useUniversalAuth();
  const { inboxes } = useInboxes();

  const [channels, setChannels] = useState<SlackChannel[]>([]);
  const [selectedChannel, setSelectedChannel] = useState<string>("");
  const [channelSearch, setChannelSearch] = useState<string>("");
  const [comment, setComment] = useState<string>("");
  const [channelsLoading, setChannelsLoading] = useState(false);
  const [isSharing, setIsSharing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selectedAttachmentIds, setSelectedAttachmentIds] = useState<Set<string>>(new Set());

  const {
    data: attachments = [],
    isLoading: attachmentsLoading,
  } = useMessageAttachments(message, open);

  // The connection id lives on the inbox, not the message.
  const connectionId = useMemo(() => {
    const inbox = inboxes.find((i) => i.id === message.inboxId);
    return inbox?.connectionId ?? null;
  }, [inboxes, message.inboxId]);

  // Reset transient state each time the modal opens.
  useEffect(() => {
    if (!open) return;
    setError(null);
    setComment("");
    setSelectedChannel("");
    setChannelSearch("");
    setSelectedAttachmentIds(new Set());
  }, [open]);

  // Fetch available Slack channels.
  useEffect(() => {
    if (!open) return;
    const fetchChannels = async () => {
      try {
        setChannelsLoading(true);
        setError(null);
        const response = await fetch("/api/slack/channels");
        if (!response.ok) throw new Error("Failed to fetch Slack channels");
        const data = await response.json();
        setChannels((data.channels as SlackChannel[]) || []);
      } catch (err) {
        setError(err instanceof Error ? err.message : "Unknown error");
      } finally {
        setChannelsLoading(false);
      }
    };
    void fetchChannels();
  }, [open]);

  const toggleAttachment = useCallback((attachmentId: string) => {
    setSelectedAttachmentIds((prev) => {
      const next = new Set(prev);
      if (next.has(attachmentId)) next.delete(attachmentId);
      else next.add(attachmentId);
      return next;
    });
  }, []);

  const selectAll = useCallback(() => {
    setSelectedAttachmentIds(new Set(attachments.map((a: { id: string }) => a.id)));
  }, [attachments]);

  const deselectAll = useCallback(() => {
    setSelectedAttachmentIds(new Set());
  }, []);

  const handleClose = useCallback(() => {
    onOpenChange(false);
  }, [onOpenChange]);

  const handleShare = useCallback(async () => {
    if (!selectedChannel) {
      setError("Please select a channel");
      return;
    }
    if (selectedAttachmentIds.size === 0) {
      setError("Please select at least one attachment");
      return;
    }
    if (!connectionId) {
      setError("Could not resolve this message's mailbox connection");
      return;
    }
    if (!session?.access_token) {
      setError("No authentication token found");
      return;
    }

    setIsSharing(true);
    setError(null);
    try {
      const response = await fetch("/api/slack/share-files", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${session.access_token}`,
        },
        body: JSON.stringify({
          channelId: selectedChannel,
          comment: comment.trim() || undefined,
          source: { kind: "Email", label: message.subject },
          crmEmail: {
            connectionId,
            messageId: message.providerMessageId,
            attachmentIds: Array.from(selectedAttachmentIds),
          },
        }),
      });

      const data = await response.json();
      if (!response.ok) {
        throw new Error(data.error || "Failed to share attachments to Slack");
      }

      const channelName = channels.find((c) => c.id === selectedChannel)?.name ?? "the channel";
      const uploaded = typeof data.uploaded === "number" ? data.uploaded : 0;
      toast.success(`Shared ${uploaded} file${uploaded === 1 ? "" : "s"} to #${channelName}`);

      if (Array.isArray(data.errors) && data.errors.length > 0) {
        toast.warning(`${data.errors.length} file(s) could not be shared.`);
      }
      handleClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Unknown error");
    } finally {
      setIsSharing(false);
    }
  }, [
    selectedChannel,
    selectedAttachmentIds,
    connectionId,
    session,
    comment,
    message,
    channels,
    handleClose,
  ]);

  return (
    <ResponsiveModal
      open={open}
      onOpenChange={onOpenChange}
      title="Share attachments to Slack"
      description={`Share attachments from: ${message.subject}`}
      globalModalId={globalModalId}
    >
      <div className="space-y-4 py-2 max-h-[70vh] overflow-y-auto pr-2">
        {attachmentsLoading ? (
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin" />
            Loading attachments...
          </div>
        ) : attachments.length === 0 ? (
          <p className="text-sm text-muted-foreground">No attachments found in this message.</p>
        ) : (
          <AttachmentSelectionList
            attachments={attachments}
            onDeselectAll={deselectAll}
            onSelectAll={selectAll}
            onToggleAttachment={toggleAttachment}
            selectedAttachmentIds={selectedAttachmentIds}
          />
        )}

        <div className="space-y-2">
          <label className="text-sm font-medium">Select Channel</label>
          <ResponsiveCommandSelect
            value={selectedChannel}
            onValueChange={setSelectedChannel}
            options={channels.map((channel) => ({
              value: channel.id,
              label: `${channel.is_private ? "🔒" : "#"}${channel.name}`,
              searchableText: channel.name,
            }))}
            placeholder={
              channelsLoading
                ? "Loading channels..."
                : channels.length === 0
                  ? "No channels available"
                  : `Search ${channels.length} channels...`
            }
            searchPlaceholder="Type to search channels..."
            disabled={channelsLoading || channels.length === 0}
            searchValue={channelSearch}
            onSearchChange={setChannelSearch}
            title="Select Slack Channel"
            description="The attachments are uploaded to this channel."
          />
        </div>

        <div className="space-y-2">
          <label className="text-sm font-medium">Comment (optional)</label>
          <Input
            value={comment}
            onChange={(e) => setComment(e.target.value)}
            placeholder="Add a note for the channel..."
            maxLength={3000}
            disabled={isSharing}
          />
        </div>

        {error && (
          <div className="rounded-md bg-destructive/10 p-3 text-sm text-destructive">{error}</div>
        )}

        <div className="flex justify-end gap-2 pt-2">
          <Button variant="outline" onClick={handleClose} disabled={isSharing}>
            Cancel
          </Button>
          <Button
            onClick={handleShare}
            disabled={
              isSharing ||
              channelsLoading ||
              attachmentsLoading ||
              !selectedChannel ||
              selectedAttachmentIds.size === 0
            }
          >
            {isSharing ? (
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            ) : (
              <Slack className="mr-2 h-4 w-4" />
            )}
            {isSharing ? "Sharing..." : "Share to Slack"}
          </Button>
        </div>
      </div>
    </ResponsiveModal>
  );
}
